import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { finalize } from 'rxjs';

import { containersQuery, containersService, ManageContainer } from '@store/settings/containers';

import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';

import ModalComponent from '@components/modal/Modal.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';

import { Colors } from '@constants/colors.constants';

interface ManageContainerModalProps {
  isCreation?: boolean;
}

const ManageContainerModal = (props: ManageContainerModalProps) => {
  const { isCreation } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { containerId } = useParams();

  const [loading, setLoading] = useState(false);

  const [container, setContainer] = useState<ManageContainer>({});

  useEffect(() => {
    if (containerId)
      setContainer(containersQuery.containerById(+containerId) || {});
  }, [containerId]);

  if (!isCreation && !container.id) return null;

  const updateContainer = (name: string) => (value: any) => setContainer((state) => ({ ...state, [name]: value }));

  const handleClose = () => navigate('..', { replace: true });

  const handleSave = () => {
    if (isCreation) {
      setLoading(true);
      containersService.createContainer(container).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.containers.success.add'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!containerId) return;
      setLoading(true);
      containersService.updateContainer(containerId, container).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.containers.success.edit'), { variant: 'success' });
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const errors = {
    name: getFieldError(container.name, FieldValidationType.REQUIRED_STRING),
    volume: getFieldError(container.volume, FieldValidationType.REQUIRED_NUMBER)
  };

  return (
    <ModalComponent
      title={t(`settings.containers.manage.title.${isCreation ? 'add' : 'edit'}`)}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.containers.manage.name')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContainer('name')}
            value={container.name || ''}
            error={errors.name}
            disabled={!isCreation}
            required />
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.containers.manage.volume')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
            endAdornment={(
              <Typography fontSize={16} color={Colors.textWithOpacity}>m3</Typography>
            )}
            handleChange={updateContainer('volume')}
            value={container.volume ?? undefined}
            error={errors.volume}
            required />
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('global.translateKey')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContainer('nameTranslateKey')}
            value={container.nameTranslateKey ?? 'fe.settings.containers.'} />
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.containers.manage.icon')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContainer('iconPath')}
            value={container.iconPath ?? undefined} />
        </Stack>
      )}
      actions={(
        <Button
          variant="contained"
          disabled={loading || checkIfErrors(errors)}
          onClick={handleSave}
          color="primary">
          {t('global.save')}
        </Button>
      )} />
  );
};

export default ManageContainerModal;
